import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import PropTypes from "prop-types";
import React, { useState } from "react";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import InfoIcon from "@material-ui/icons/Info";
import ErrorIcon from "@material-ui/icons/Error";
import { useTranslation } from "react-i18next";
import {
  StyledTableCell,
  StyledTableRow,
  useStyles,
} from "components/profiles/ProfileStyles";
import { screenStyles } from "./Styles";
import { translateBillStatus, BillStatus } from "./ScreenUtils";
import Actions from "./UserBillsTableActions";
import { FormatDateToFront } from "utils/dates/FormatDateToFront";
import { GetMonthAndYearInWords } from "utils/dates/GetMonthAnsYearInWords";
import { valueToMoneyFloat } from "utils/general/valueToMoneyFloat";
import { moneyMask } from "utils/masks/moneyMask";
import { Box } from "@material-ui/core";
import { compareStrings } from "utils/sort/strings";
import { createContent } from "utils/tables/createContent";

function desc(a, b, orderBy) {
  if (a[orderBy] === null || a[orderBy] === undefined) {
    a[orderBy] = "";
  }
  if (b[orderBy] === null || b[orderBy] === undefined) {
    b[orderBy] = "";
  }

  if (orderBy === "date" || orderBy === "value") {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;

    return 0;
  }

  if (orderBy === "type") {
    if (translateBillStatus(b[orderBy]) < translateBillStatus(a[orderBy]))
      return -1;
    if (translateBillStatus(b[orderBy]) > translateBillStatus(a[orderBy]))
      return 1;

    return 0;
  }

  return compareStrings(a[orderBy].toString(), b[orderBy].toString());
}

const BillsTable = props => {
  const styles = screenStyles();
  const classes = useStyles();
  const [t] = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState({});
  const [rowsLength, setRowsLength] = useState(0);
  const [headers, setHeaders] = useState([]);
  const [mainField, setMainField] = useState("");
  const [visibleHeaders, setVisibleHeaders] = useState([]);
  const [sortedFields, setSortedFields] = useState([]);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");

  React.useEffect(() => {
    setHeaders(props.headers);
    setRows(props.rows);
    setRowsLength(props.rows.length);
    setMainField(props.mainField);
    setVisibleHeaders(props.visibleHeaders);
    setSortedFields(props.sortedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.headers, props.mainField, props.rows, props.visibleHeaders]);

  function handlePageChange(event, newPage) {
    setPage(newPage);
  }
  const handleRowsPerPageChange = event => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === sortedFields[property] && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(sortedFields[property]);
  };

  const UsersTableHead = props => {
    const { order, orderBy, onRequestSort } = props;
    const [t] = useTranslation();
    const paginationTop = action => e => {
      var selector = "[aria-label^='" + action + "']";
      document.querySelector(selector).click();
      e.preventDefault();
    };
    const createSortHandler = property => event => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          {visibleHeaders.map((header, index) => (
            <StyledTableCell
              key={index}
              size="small"
              align="left"
              sortDirection={orderBy === sortedFields[index] ? order : false}
            >
              {index !== 3 ? (
                <TableSortLabel
                  active={orderBy === sortedFields[index]}
                  direction={order}
                  onClick={createSortHandler(index)}
                >
                  {header.toUpperCase()}
                  {orderBy === sortedFields[index] ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              ) : null}
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  UsersTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
  };

  React.useEffect(() => {
    if (props.toFirstPage) {
      document.getElementById("toFirstPage").click();
    }
  }, [props.rows, props.toFirstPage]);

  const toFirstPage = () => {
    setPage(0);
  };

  return (
    <Paper className={classes.root}>
      <div className={classes.tableWrapper}>
        <Table className={classes.table} size="small">
          {rows.length > 0 && (
            <UsersTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
          )}
          <TableBody>
            {createContent(rows, headers, order, orderBy, desc)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(row => (
                <StyledTableRow key={row.date}>
                  <TableCell size="small" width={"60%"}>
                    {row["date"]
                      ? GetMonthAndYearInWords(FormatDateToFront(row["date"]))
                      : "-"}
                  </TableCell>

                  <TableCell size="small">
                    {row["status"] ? (
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        minHeight="100%"
                      >
                        {row["status"] === BillStatus.PAID ? (
                          <CheckCircleIcon
                            style={{
                              color: "#7FE46B",
                            }}
                            fontSize="md"
                          />
                        ) : row["status"] === BillStatus.PENDING ? (
                          <ErrorIcon
                            style={{
                              color: "#FFD200",
                            }}
                            fontSize="md"
                          />
                        ) : row["status"] === BillStatus.LATE ? (
                          <ErrorIcon
                            style={{
                              color: "#F3222F",
                            }}
                            fontSize="md"
                          />
                        ) : (
                          <InfoIcon
                            style={{
                              color: "#6D6F71",
                            }}
                            fontSize="md"
                          />
                        )}
                        <Box className={styles.smallMargin}>
                          {translateBillStatus(row["status"])}
                        </Box>
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  <TableCell size="small">
                    {row["value"] ? (
                      <Box
                        color={
                          row["status"] === BillStatus.PAID
                            ? "#27AE60"
                            : row["status"] === BillStatus.LATE
                            ? "#F3222F"
                            : null
                        }
                        fontWeight="700"
                      >
                        {`R$
                                                ${moneyMask(
                                                  valueToMoneyFloat(
                                                    row["value"]
                                                  )
                                                )}
                                                `}
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  <TableCell size="small" align="right">
                    <div
                      style={{
                        marginTop: "auto",
                        marginBottom: "auto",
                        display: "flex",
                        flexWrap: "nowrap ",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Actions
                        item={row}
                        name={row.shortname}
                        context={props.context}
                        permission={props.permission}
                        fetchData={props.fetchData}
                        activityCategories={props.activityCategories}
                        submit={props.submit}
                      />
                    </div>
                  </TableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
        <button hidden id="toFirstPage" onClick={toFirstPage}></button>
        {rows.length > 0 && (
          <TableFooter style={{ float: "right" }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              labelRowsPerPage={"Resultados por página"}
              count={rowsLength}
              rowsPerPage={rowsPerPage}
              page={page}
              component="div"
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </TableFooter>
        )}
      </div>
    </Paper>
  );
};

BillsTable.propTypes = {
  rows: PropTypes.array.isRequired,
  headers: PropTypes.array.isRequired,
  visibleHeaders: PropTypes.array.isRequired,
  mainField: PropTypes.string.isRequired,
};

export default BillsTable;
